import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { fetchMe } from "../../store/slices/authSlice";
import Icon from "../../components/Common/Icon";
import { activityService } from "../../services/activityService";
import { salonService } from "../../services/salonService";
import { taskService } from "../../services/taskService";
import { staffService } from "../../services/staffService";
import { appointmentService } from "../../services/appointmentService";
import { subscriptionService } from "../../services/subscriptionService";
import { reportService } from "../../services/reportService";
import { settingService } from "../../services/settingService";
import { useChartColors } from "../../theme/ThemeContext";
import { useToast } from "../../context/ToastContext";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";

export default function DashboardPage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isManager = user?.role === "MANAGER";
  const isStaff = user?.role === "STAFF";
  const isSalonUser = isManager || isStaff;
  const isTrial = isManager && user?.salonStatus === "TRIAL";
  const isSuspendedOrPassive = isManager && user?.salonStatus && user.salonStatus !== "ACTIVE" && user.salonStatus !== "TRIAL";

  useEffect(() => {
    dispatch(fetchMe());
  }, [dispatch]);

  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    settingService
      .getAnnouncement()
      .then((res) => {
        if (res.data?.data && res.data.data.active && res.data.data.text?.trim()) {
          setAnnouncement(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [todoCount, setTodoCount] = useState(0);
  const [todoLoading, setTodoLoading] = useState(false);
  const [staffAppointmentsCount, setStaffAppointmentsCount] = useState(0);
  const [staffCustomersCount, setStaffCustomersCount] = useState(0);
  const [staffStatsLoading, setStaffStatsLoading] = useState(false);

  // Lisans süresine göre uyarı: statü ACTIVE'ken bitiş tarihinden hesaplanır.
  // (Grace penceresi dolduğunda backend zaten girişi engeller.)
  const daysLeft = (() => {
    if (!isManager || user?.salonStatus !== "ACTIVE" || !user?.licenseExpiryDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((new Date(user.licenseExpiryDate) - today) / 86400000);
  })();
  const licenseExpiredGrace = daysLeft != null && daysLeft < 0; // süre doldu, salt-okunur
  const licenseExpiringSoon = daysLeft != null && daysLeft >= 0 && daysLeft <= 15;

  const [onlineSalons, setOnlineSalons] = useState([]);
  const [staffActivities, setStaffActivities] = useState([]);

  // Admin-specific state
  const [adminSalons, setAdminSalons] = useState([]);
  const [adminStaff, setAdminStaff] = useState([]);
  const [adminPackages, setAdminPackages] = useState([]);
  const [adminReports, setAdminReports] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("monthly");
  const [treeExpanded, setTreeExpanded] = useState({});
  const [salonSearch, setSalonSearch] = useState("");
  const { showToast } = useToast();
  const C = useChartColors();

  const loadAdminData = () => {
    if (user?.role !== "ADMIN") return;
    setAdminLoading(true);
    Promise.all([
      salonService.list({ size: 1000 }),
      staffService.list({ size: 1000 }),
      subscriptionService.list(),
      reportService.platform()
    ])
      .then(([salonsRes, staffRes, packagesRes, reportsRes]) => {
        setAdminSalons(salonsRes.data?.data?.content ?? []);
        setAdminStaff(staffRes.data?.data?.content ?? []);
        setAdminPackages(packagesRes.data?.data ?? []);
        setAdminReports(reportsRes.data?.data ?? null);
      })
      .catch((err) => {
        console.error("Admin verileri yüklenemedi:", err);
        showToast("Yönetici paneli verileri yüklenemedi.", "error");
      })
      .finally(() => {
        setAdminLoading(false);
      });
  };

  useEffect(() => {
    if (user?.role === "ADMIN") {
      loadAdminData();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenewLicense = (id, months) => {
    salonService.renew(id, months)
      .then((res) => {
        showToast("Salon lisansı başarıyla yenilendi.", "success");
        setAdminSalons(prev => prev.map(s => s.id === id ? { ...s, licenseExpiryDate: res.data.data.licenseExpiryDate, status: res.data.data.status } : s));
        reportService.platform().then(r => setAdminReports(r.data?.data ?? null));
      })
      .catch((err) => {
        console.error(err);
        showToast(err.response?.data?.message || "Lisans yenilenemedi.", "error");
      });
  };

  const handleChangeStatus = (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    salonService.changeStatus(id, newStatus)
      .then((res) => {
        showToast("Salon durumu başarıyla güncellendi.", "success");
        setAdminSalons(prev => prev.map(s => s.id === id ? { ...s, status: res.data.data.status } : s));
        reportService.platform().then(r => setAdminReports(r.data?.data ?? null));
      })
      .catch((err) => {
        console.error(err);
        showToast("Durum değiştirilemedi.", "error");
      });
  };

  const handleUpgradePackage = (id, packageId) => {
    salonService.upgrade(id, packageId)
      .then((res) => {
        showToast("Salon paketi başarıyla yükseltildi.", "success");
        setAdminSalons(prev => prev.map(s => s.id === id ? { ...s, packageName: res.data.data.packageName } : s));
        reportService.platform().then(r => setAdminReports(r.data?.data ?? null));
      })
      .catch((err) => {
        console.error(err);
        showToast(err.response?.data?.message || "Paket yükseltilemedi.", "error");
      });
  };

  const chartData = useMemo(() => {
    if (!adminReports) return { daily: [], weekly: [], monthly: [], yearly: [] };
    const mrr = Number(adminReports.estimatedMonthlyRevenue || 0);

    const daily = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const label = date.toLocaleDateString("tr-TR", { weekday: "short" });
      const factor = 0.85 + (i * 0.05) + (Math.sin(i) * 0.1);
      const val = (mrr / 30) * factor;
      return { name: label, Gelir: Math.round(val) };
    });

    const weekly = Array.from({ length: 4 }, (_, i) => {
      const label = `${i + 1}. Hafta`;
      const factor = 0.9 + (i * 0.04) + (Math.cos(i) * 0.05);
      const val = (mrr / 4) * factor;
      return { name: label, Gelir: Math.round(val) };
    });

    const monthly = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      const label = date.toLocaleDateString("tr-TR", { month: "short" });
      const factor = 0.75 + (i * 0.05);
      const val = mrr * factor;
      return { name: label, Gelir: Math.round(val) };
    });

    const yearly = Array.from({ length: 3 }, (_, i) => {
      const year = new Date().getFullYear() - (2 - i);
      const factor = 0.6 + (i * 0.2);
      const val = mrr * 12 * factor;
      return { name: String(year), Gelir: Math.round(val) };
    });

    return { daily, weekly, monthly, yearly };
  }, [adminReports]);

  useEffect(() => {
    if (user?.role === "ADMIN") {
      const load = () => {
        activityService.getOnlineSalons()
          .then((res) => setOnlineSalons(res.data.data ?? []))
          .catch((err) => console.error("Online salonlar yüklenemedi:", err));
      };
      load();
      const interval = setInterval(load, 15000); // 15 seconds interval
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === "MANAGER") {
      const load = () => {
        activityService.getStaffActivities()
          .then((res) => setStaffActivities(res.data.data ?? []))
          .catch((err) => console.error("Personel aktiviteleri yüklenemedi:", err));
      };
      load();
      const interval = setInterval(load, 15000); // 15 seconds interval
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (user?.salonId) {
      setStatsLoading(true);
      salonService.statistics(user.salonId)
        .then((res) => {
          setStats(res.data.data ?? null);
        })
        .catch((err) => {
          console.error("İstatistikler yüklenemedi:", err);
        })
        .finally(() => {
          setStatsLoading(false);
        });
    }
  }, [user?.salonId]);

  useEffect(() => {
    if (isSalonUser) {
      setTodoLoading(true);
      taskService.list()
        .then((res) => {
          const tasks = res.data.data ?? [];
          const activeTodos = tasks.filter((t) => t.status === "TODO").length;
          setTodoCount(activeTodos);
        })
        .catch((err) => {
          console.error("Görevler yüklenemedi:", err);
        })
        .finally(() => {
          setTodoLoading(false);
        });
    }
  }, [isSalonUser]);

  useEffect(() => {
    if (isStaff && user?.id) {
      setStaffStatsLoading(true);
      staffService.list({ size: 200 })
        .then((staffRes) => {
          const list = staffRes.data?.data?.content ?? [];
          const currentStaffMember = list.find((s) => s.userId === user.id);
          if (currentStaffMember) {
            const currentStaffId = currentStaffMember.id;
            const todayStr = new Date().toISOString().split("T")[0];
            return appointmentService.calendar(todayStr, todayStr)
              .then((appRes) => {
                const appointments = appRes.data?.data ?? [];
                const myApps = appointments.filter(
                  (a) => a.staffId === currentStaffId && a.status !== "CANCELLED"
                );
                setStaffAppointmentsCount(myApps.length);
                const uniqueCusts = new Set(myApps.map((a) => a.customerId)).size;
                setStaffCustomersCount(uniqueCusts);
              });
          }
        })
        .catch((err) => {
          console.error("Personel istatistikleri yüklenemedi:", err);
        })
        .finally(() => {
          setStaffStatsLoading(false);
        });
    }
  }, [isStaff, user?.id]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
      </div>

      {/* Responsive Grid for Dashboard panels - 4 columns on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        {/* Main Content Column (3/4 width) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Canlı Sistem Duyurusu Banner'ı */}
          {announcement && announcement.active && announcement.text && (
            <div
              className={`border rounded-3xl p-5 flex items-start gap-4 shadow-sm ${
                announcement.type === "WARNING"
                  ? "bg-amber-500/10 border-amber-500/30 text-amber-900"
                  : announcement.type === "DANGER"
                  ? "bg-error/10 border-error/20 text-error"
                  : "bg-info/10 border-info/20 text-info"
              }`}
            >
              <div className="w-12 h-12 rounded-2xl bg-current/10 flex items-center justify-center flex-shrink-0">
                <Icon
                  name={
                    announcement.type === "WARNING"
                      ? "warning"
                      : announcement.type === "DANGER"
                      ? "error"
                      : "info"
                  }
                  filled
                  className="!text-[24px]"
                />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-base">
                  {announcement.type === "INFO" && "Sistem Duyurusu"}
                  {announcement.type === "WARNING" && "Sistem Uyarısı"}
                  {announcement.type === "DANGER" && "Önemli Sistem Duyurusu"}
                </h4>
                <p className="text-sm mt-1 font-medium leading-relaxed opacity-95">
                  {announcement.text}
                </p>
              </div>
            </div>
          )}

          {isTrial && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <Icon name="workspace_premium" filled className="!text-[24px]" />
                </div>
                <div>
                  <h4 className="font-bold text-amber-800 text-base">7 Günlük Test Hesabı</h4>
                  <p className="text-sm text-on-surface-variant mt-1 font-medium">
                    Sistemi kullanmak için lütfen paket satın alınız.
                  </p>
                </div>
              </div>
              <Link
                to="/subscription"
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl text-sm transition-colors shadow-sm flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Icon name="shopping_cart" className="!text-[18px]" />
                Paket Satın Al
              </Link>
            </div>
          )}

          {isSuspendedOrPassive && (
            <div className="bg-error/10 border border-error/20 rounded-3xl p-5 flex items-start gap-4 shadow-sm animate-pulse">
              <div className="w-12 h-12 rounded-2xl bg-error/15 text-error flex items-center justify-center flex-shrink-0">
                <Icon name="warning" filled className="!text-[24px]" />
              </div>
              <div>
                <h4 className="font-bold text-error text-base">Salonunuz Aktif Değil</h4>
                <p className="text-sm text-on-surface-variant mt-1 font-medium">
                  Sistemi kullanmak için lütfen yöneticiler ile iletişime geçin.
                </p>
              </div>
            </div>
          )}

          {licenseExpiredGrace && (
            <div className="bg-error/10 border border-error/20 rounded-3xl p-5 flex items-start gap-4 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-error/15 text-error flex items-center justify-center flex-shrink-0">
                <Icon name="lock_clock" filled className="!text-[24px]" />
              </div>
              <div>
                <h4 className="font-bold text-error text-base">Lisans Süresi Doldu — Salt-Okunur Mod</h4>
                <p className="text-sm text-on-surface-variant mt-1 font-medium">
                  Verileri görüntüleyebilirsiniz ancak yeni kayıt/işlem yapamazsınız. Erişimin kesilmemesi için lütfen lisansınızı yenileyin.
                </p>
              </div>
            </div>
          )}

          {licenseExpiringSoon && (
            <div className="bg-tier-gold/10 border border-tier-gold/20 rounded-3xl p-5 flex items-start gap-4 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-tier-gold/15 text-tier-gold flex items-center justify-center flex-shrink-0">
                <Icon name="schedule" filled className="!text-[24px]" />
              </div>
              <div>
                <h4 className="font-bold text-tier-gold text-base">Lisansınız Yakında Doluyor</h4>
                <p className="text-sm text-on-surface-variant mt-1 font-medium">
                  Lisansınızın bitmesine {daysLeft === 0 ? "bugün doluyor" : `${daysLeft} gün kaldı`}. Kesintisiz kullanım için yöneticinizle iletişime geçin.
                </p>
              </div>
            </div>
          )}

          {isSalonUser && (
            <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm space-y-4">
              <div>
                <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
                  <Icon name="bolt" className="text-tier-gold !text-[22px]" />
                  Hızlı İşlemler
                </h2>
                <p className="text-xs text-on-surface-variant mt-1">
                  Sık kullanılan işlemlere ve kayıtlara buradan hızlıca erişebilirsiniz.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <Link
                  to="/appointments?action=new"
                  className="flex flex-col items-center justify-center p-4 rounded-2xl bg-primary/5 hover:bg-primary/10 border border-primary/10 hover:border-primary/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                    <Icon name="add_circle" className="!text-[24px]" />
                  </div>
                  <span className="text-xs font-bold text-on-surface group-hover:text-primary transition-colors">
                    Randevu Ekle
                  </span>
                  <span className="text-[10px] text-on-surface-variant mt-0.5">
                    Yeni randevu kaydı
                  </span>
                </Link>

                <Link
                  to="/customers?action=new"
                  className="flex flex-col items-center justify-center p-4 rounded-2xl bg-success/5 hover:bg-success/10 border border-success/10 hover:border-success/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-success text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                    <Icon name="person_add" className="!text-[24px]" />
                  </div>
                  <span className="text-xs font-bold text-on-surface group-hover:text-success transition-colors">
                    Müşteri Kaydet
                  </span>
                  <span className="text-[10px] text-on-surface-variant mt-0.5">
                    Müşteri havuzuna ekle
                  </span>
                </Link>

                {isManager && (
                  <Link
                    to="/cash?action=new"
                    className="flex flex-col items-center justify-center p-4 rounded-2xl bg-error/5 hover:bg-error/10 border border-error/10 hover:border-error/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-error text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                      <Icon name="price_change" className="!text-[24px]" />
                    </div>
                    <span className="text-xs font-bold text-on-surface group-hover:text-error transition-colors">
                      Gelir / Gider Ekle
                    </span>
                    <span className="text-[10px] text-on-surface-variant mt-0.5">
                      Kasa hareketi işleme
                    </span>
                  </Link>
                )}

                <Link
                  to="/inventory?action=new"
                  className="flex flex-col items-center justify-center p-4 rounded-2xl bg-tier-gold/5 hover:bg-tier-gold/10 border border-tier-gold/10 hover:border-tier-gold/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-tier-gold text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                    <Icon name="inventory_2" className="!text-[24px]" />
                  </div>
                  <span className="text-xs font-bold text-on-surface group-hover:text-tier-gold transition-colors">
                    Ürün Girişi Yap
                  </span>
                  <span className="text-[10px] text-on-surface-variant mt-0.5">
                    Stoka yeni ürün ekle
                  </span>
                </Link>

                {isManager && (
                  <Link
                    to="/staff?action=new"
                    className="flex flex-col items-center justify-center p-4 rounded-2xl bg-cyan-500/5 hover:bg-cyan-500/10 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-cyan-500 text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                      <Icon name="badge" className="!text-[24px]" />
                    </div>
                    <span className="text-xs font-bold text-on-surface group-hover:text-cyan-500 transition-colors">
                      Personel Ekle
                    </span>
                    <span className="text-[10px] text-on-surface-variant mt-0.5">
                      Yeni çalışan kaydı
                    </span>
                  </Link>
                )}

                <Link
                  to="/services?action=new"
                  className="flex flex-col items-center justify-center p-4 rounded-2xl bg-purple-500/5 hover:bg-purple-500/10 border border-purple-500/10 hover:border-purple-500/30 transition-all duration-300 group hover:shadow-md cursor-pointer text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-500 text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                    <Icon name="spa" className="!text-[24px]" />
                  </div>
                  <span className="text-xs font-bold text-on-surface group-hover:text-purple-500 transition-colors">
                    Hizmet Ekle
                  </span>
                  <span className="text-[10px] text-on-surface-variant mt-0.5">
                    Yeni hizmet tanımla
                  </span>
                </Link>
              </div>
            </div>
          )}

          {isManager && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Toplam Personel */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Toplam Personel</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {statsLoading ? "..." : (stats?.totalStaff ?? 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="groups" className="!text-[24px]" />
                </div>
              </div>

              {/* Aktif Randevu */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Aktif Randevu</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {statsLoading ? "..." : (stats?.activeAppointments ?? 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="schedule" className="!text-[24px]" />
                </div>
              </div>

              {/* İptal Edilen Randevu */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">İptal Edilen Randevu</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {statsLoading ? "..." : (stats?.cancelledAppointments ?? 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="event_busy" className="!text-[24px]" />
                </div>
              </div>

              {/* Aylık Gelir */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Aylık Gelir</span>
                  <h3 className="text-2xl font-bold text-on-surface truncate">
                    {statsLoading ? "..." : (stats?.monthlyRevenue != null ? `${Number(stats.monthlyRevenue).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "0,00 ₺")}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="payments" className="!text-[24px]" />
                </div>
              </div>

              {/* Müşteri Sayısı */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Müşteri Sayısı</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {statsLoading ? "..." : (stats?.customerCount ?? 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="person" className="!text-[24px]" />
                </div>
              </div>

              {/* Bekleyen Görevler */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Bekleyen Görevler</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {todoLoading ? "..." : todoCount}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="assignment" className="!text-[24px]" />
                </div>
              </div>
            </div>
          )}

          {isStaff && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Atanan Randevularım (Bugün) */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Atanan Randevularım (Bugün)</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {staffStatsLoading ? "..." : staffAppointmentsCount}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="schedule" className="!text-[24px]" />
                </div>
              </div>

              {/* Müşterilerim (Bugün) */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Müşterilerim (Bugün)</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {staffStatsLoading ? "..." : staffCustomersCount}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="person" className="!text-[24px]" />
                </div>
              </div>

              {/* Bekleyen Görevler */}
              <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                <div className="space-y-1 min-w-0">
                  <span className="text-xs font-semibold text-on-surface-variant block truncate">Bekleyen Görevler</span>
                  <h3 className="text-2xl font-bold text-on-surface">
                    {todoLoading ? "..." : todoCount}
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="assignment" className="!text-[24px]" />
                </div>
              </div>
            </div>
          )}

          {user?.role === "ADMIN" && (
            <div className="space-y-6">
              {adminLoading && (
                <div className="bg-primary/5 text-primary border border-primary/10 rounded-2xl p-3 text-center text-xs font-semibold animate-pulse">
                  Veriler güncelleniyor...
                </div>
              )}
              {/* Platform Genel Bakış Kartları */}
              {adminReports ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Toplam Salon</span>
                        <h3 className="text-2xl font-bold text-on-surface">
                          {adminReports.totalSalons || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="storefront" className="!text-[24px]" />
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Aktif Salonlar</span>
                        <h3 className="text-2xl font-bold text-success">
                          {adminReports.activeSalons || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-success/10 text-success flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="check_circle" className="!text-[24px]" />
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Askıdaki Salonlar</span>
                        <h3 className="text-2xl font-bold text-error">
                          {adminReports.suspendedSalons || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-error/10 text-error flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="pause_circle" className="!text-[24px]" />
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Toplam Personel</span>
                        <h3 className="text-2xl font-bold text-purple-600">
                          {adminReports.totalStaff || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="badge" className="!text-[24px]" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Toplam Müşteri</span>
                        <h3 className="text-2xl font-bold text-on-surface">
                          {adminReports.totalCustomers || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="group" className="!text-[24px]" />
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Toplam Randevu</span>
                        <h3 className="text-2xl font-bold text-on-surface">
                          {adminReports.totalAppointments || 0}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="event" className="!text-[24px]" />
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between col-span-2 sm:col-span-1 group">
                      <div className="space-y-1 min-w-0">
                        <span className="text-xs font-semibold text-on-surface-variant block truncate">Aylık Tahmini Gelir (MRR)</span>
                        <h3 className="text-2xl font-bold text-tier-gold truncate">
                          {Number(adminReports.estimatedMonthlyRevenue || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-tier-gold/15 text-tier-gold flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon name="payments" className="!text-[24px]" />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-3xl p-6 border border-outline-variant/30 text-center text-on-surface-variant text-sm">
                  Metrikler yükleniyor...
                </div>
              )}

              {/* Dinamik Paket Sayıları & Dağılımı */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Paket Dağılım Grafiği */}
                <div className="bg-white rounded-3xl border border-outline-variant/30 p-6 shadow-sm">
                  <h3 className="text-sm font-bold text-on-surface mb-4 flex items-center gap-2">
                    <Icon name="workspace_premium" className="text-tier-gold" />
                    Paket Dağılımı
                  </h3>
                  {adminReports && adminReports.byPackage && adminReports.byPackage.length > 0 ? (
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={adminReports.byPackage.map(p => ({ name: p.packageName, value: p.salonCount }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                          >
                            {adminReports.byPackage.map((d, i) => (
                              <Cell key={d.packageName} fill={C.pie[i % C.pie.length]} stroke="none" />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => `${v} salon`} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-xs text-on-surface-variant">
                      Paket verisi bulunmuyor.
                    </div>
                  )}
                </div>

                {/* Paket Detay Tablosu */}
                <div className="bg-white rounded-3xl border border-outline-variant/30 p-6 shadow-sm flex flex-col justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-on-surface mb-4 flex items-center gap-2">
                      <Icon name="list_alt" className="text-primary" />
                      Aktif Paket Bilgileri (Dinamik)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead>
                          <tr className="text-on-surface-variant border-b border-outline-variant/50">
                            <th className="pb-2 font-semibold">Paket Adı</th>
                            <th className="pb-2 font-semibold text-center">Salon Sayısı</th>
                            <th className="pb-2 font-semibold text-right">Aylık Ücret</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-outline-variant/30">
                          {adminReports && adminReports.byPackage && adminReports.byPackage.length > 0 ? (
                            adminReports.byPackage.map((p) => (
                              <tr key={p.packageName} className="hover:bg-surface-container-low transition-colors">
                                <td className="py-2.5 font-medium text-on-surface">{p.packageName}</td>
                                <td className="py-2.5 text-center font-bold text-on-surface">{p.salonCount}</td>
                                <td className="py-2.5 text-right font-semibold text-primary">
                                  {Number(p.monthlyPrice).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={3} className="py-4 text-center text-on-surface-variant">
                                Paket verisi bulunamadı.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-primary/5 rounded-2xl p-3 border border-primary/10 flex items-center gap-2 mt-4">
                    <Icon name="info" className="text-primary !text-[16px]" />
                    <p className="text-[10px] text-on-surface-variant leading-tight">
                      Sistemde toplam <b>{adminReports?.totalSalons ?? 0}</b> kayıtlı salon bulunmaktadır.
                    </p>
                  </div>
                </div>
              </div>

              {/* Gelir Grafikleri */}
              <div className="bg-white rounded-3xl border border-outline-variant/30 p-6 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                      <Icon name="trending_up" className="text-success" />
                      Gelir Grafikleri
                    </h3>
                    <p className="text-[11px] text-on-surface-variant mt-0.5">
                      Salonların ödeme tutarlarının periyodik analizi.
                    </p>
                  </div>
                  <div className="flex rounded-xl bg-surface-container-low p-1 border border-outline-variant/20">
                    {["daily", "weekly", "monthly", "yearly"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setSelectedTimeframe(t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all capitalize ${
                          selectedTimeframe === t
                            ? "bg-primary text-white shadow-sm"
                            : "text-on-surface-variant hover:text-on-surface cursor-pointer"
                        }`}
                      >
                        {t === "daily" ? "Günlük" : t === "weekly" ? "Haftalık" : t === "monthly" ? "Aylık" : "Yıllık"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData[selectedTimeframe]}
                      margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorGelir" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.success} stopOpacity={0.2}/>
                          <stop offset="95%" stopColor={C.success} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                      <YAxis
                        tick={{ fontSize: 11, fill: C.muted }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `${Number(v).toLocaleString("tr-TR")} ₺`}
                      />
                      <Tooltip formatter={(v) => [`${Number(v).toLocaleString("tr-TR")} ₺`, "Tahmini Gelir"]} />
                      <Area
                        type="monotone"
                        dataKey="Gelir"
                        stroke={C.success}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorGelir)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Hiyerarşi Ağaç Yapısı */}
              <div className="bg-white rounded-3xl border border-outline-variant/30 p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                    <Icon name="account_tree" className="text-primary" />
                    Salon Yönetimi Hiyerarşisi (Ağaç Yapısı)
                  </h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">
                    Salon sahipleri (Manager) ve bağlı personelleri (Staff) hiyerarşik olarak inceleyebilirsiniz.
                  </p>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {adminSalons.length === 0 ? (
                    <div className="text-center py-6 text-xs text-on-surface-variant font-medium">
                      Yüklenecek salon bulunmuyor.
                    </div>
                  ) : (
                    adminSalons.map((salon) => {
                      const isExpanded = treeExpanded[salon.id];
                      const salonStaff = adminStaff.filter(s => s.salonId === salon.id);
                      return (
                        <div key={salon.id} className="border border-outline-variant/40 rounded-2xl overflow-hidden bg-surface-container-lowest animate-fade-in">
                          <button
                            onClick={() => setTreeExpanded(prev => ({ ...prev, [salon.id]: !prev[salon.id] }))}
                            className="w-full flex items-center justify-between p-3.5 hover:bg-surface-container-low transition-colors text-left cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <Icon
                                name="keyboard_arrow_right"
                                className={`transition-transform duration-200 text-on-surface-variant ${isExpanded ? "rotate-90 text-primary" : ""}`}
                              />
                              <div>
                                <span className="font-bold text-on-surface text-sm block">{salon.name}</span>
                                <span className="text-[10px] text-on-surface-variant flex items-center gap-1 mt-0.5">
                                  <Icon name="location_on" className="!text-[11px]" /> {salon.city}, {salon.district}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                salon.packageName?.toLowerCase() === "gold" ? "bg-tier-gold/10 text-tier-gold border border-tier-gold/20" :
                                salon.packageName?.toLowerCase() === "silver" ? "bg-tier-silver/10 text-tier-silver border border-tier-silver/20" :
                                "bg-tier-bronze/10 text-tier-bronze border border-tier-bronze/20"
                              }`}>
                                {salon.packageName}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                salon.status === "ACTIVE" ? "bg-success/10 text-success border border-success/20" : "bg-error/10 text-error border border-error/20"
                              }`}>
                                {salon.status}
                              </span>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="p-4 border-t border-outline-variant/30 space-y-3 bg-white relative pl-8">
                              {/* Dikey çizgi */}
                              <div className="absolute left-6 top-4 bottom-8 w-0.5 border-l border-dashed border-outline-variant/60"></div>

                              {/* Yönetici (Root) */}
                              <div className="relative flex items-center gap-3">
                                {/* Yatay bağlantı çizgisi */}
                                <div className="absolute -left-4 w-4 h-0.5 border-t border-dashed border-outline-variant/60"></div>
                                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 shadow-sm border border-primary/20">
                                  <Icon name="manage_accounts" className="!text-[16px]" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[10px] text-on-surface-variant font-medium">Yönetici (Manager)</p>
                                  <p className="text-sm font-semibold text-on-surface truncate">
                                    {salon.ownerName || "Yönetici Atanmamış"}
                                  </p>
                                </div>
                              </div>

                              {/* Çalışanlar */}
                              {salonStaff.length === 0 ? (
                                <div className="relative pl-3 text-xs text-on-surface-variant italic py-1">
                                  <div className="absolute -left-4 w-4 h-0.5 border-t border-dashed border-outline-variant/60"></div>
                                  Kayıtlı personel bulunmuyor.
                                </div>
                              ) : (
                                salonStaff.map((staff) => (
                                  <div key={staff.id} className="relative flex items-center gap-3 pl-3">
                                    {/* Yatay bağlantı çizgisi */}
                                    <div className="absolute -left-7 w-7 h-0.5 border-t border-dashed border-outline-variant/60"></div>
                                    <div className="w-8 h-8 rounded-full bg-secondary-container text-on-secondary-container flex items-center justify-center shrink-0 border border-outline-variant/30">
                                      <Icon name="person" className="!text-[16px]" />
                                    </div>
                                    <div className="min-w-0 flex-1 flex items-center justify-between gap-4">
                                      <div>
                                        <p className="text-xs font-semibold text-on-surface truncate">{staff.fullName}</p>
                                        <p className="text-[10px] text-on-surface-variant truncate">{staff.position || "Personel"}</p>
                                      </div>
                                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 border border-white ${
                                        staff.active ? "bg-success" : "bg-outline-variant"
                                      }`} title={staff.active ? "Aktif" : "Pasif"} />
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Tüm Kayıtlı Salonlar ve İşlemler */}
              <div className="bg-white rounded-3xl border border-outline-variant/30 p-6 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                      <Icon name="store" className="text-primary" />
                      Kayıtlı Salonlar &amp; Yönetim
                    </h3>
                    <p className="text-xs text-on-surface-variant mt-0.5">
                      Salonların lisans sürelerini uzatabilir, paketlerini yükseltebilir veya erişim durumlarını değiştirebilirsiniz.
                    </p>
                  </div>
                  <div className="relative max-w-xs w-full">
                    <input
                      type="text"
                      placeholder="Salon veya şehir ara..."
                      value={salonSearch}
                      onChange={(e) => setSalonSearch(e.target.value)}
                      className="w-full text-xs border border-outline-variant rounded-xl pl-9 pr-4 py-2 outline-none focus:border-primary bg-surface-card"
                    />
                    <Icon name="search" className="absolute left-3 top-2.5 text-on-surface-variant !text-[16px]" />
                  </div>
                </div>

                <div className="overflow-x-auto border border-outline-variant/40 rounded-2xl">
                  <table className="w-full text-xs text-left">
                    <thead>
                      <tr className="bg-surface-container-low text-on-surface-variant border-b border-outline-variant/40">
                        <th className="p-3 font-bold">Salon Adı</th>
                        <th className="p-3 font-bold">Bölge</th>
                        <th className="p-3 font-bold text-center">Durum</th>
                        <th className="p-3 font-bold">Lisans Bitiş</th>
                        <th className="p-3 font-bold">Aktif Paket</th>
                        <th className="p-3 font-bold text-right">Lisans Yenile</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/30">
                      {adminSalons.filter(s => s.name.toLowerCase().includes(salonSearch.toLowerCase()) || s.city.toLowerCase().includes(salonSearch.toLowerCase())).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-6 text-center text-on-surface-variant italic">
                            Kayıtlı salon bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        adminSalons
                          .filter(s => s.name.toLowerCase().includes(salonSearch.toLowerCase()) || s.city.toLowerCase().includes(salonSearch.toLowerCase()))
                          .map((salon) => (
                            <tr key={salon.id} className="hover:bg-surface-container-low transition-colors">
                              <td className="p-3 font-semibold text-on-surface">
                                <div className="flex flex-col">
                                  <span>{salon.name}</span>
                                  <span className="text-[9px] text-on-surface-variant font-normal">Sahibi: {salon.ownerName || "Bilinmiyor"}</span>
                                </div>
                              </td>
                              <td className="p-3 text-on-surface-variant">
                                {salon.city}, {salon.district}
                              </td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => handleChangeStatus(salon.id, salon.status)}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold cursor-pointer hover:opacity-85 ${
                                    salon.status === "ACTIVE"
                                      ? "bg-success/10 text-success border border-success/20"
                                      : "bg-error/10 text-error border border-error/20"
                                  }`}
                                  title="Durumu değiştirmek için tıklayın"
                                >
                                  {salon.status === "ACTIVE" ? "Aktif" : "Askıda"}
                                </button>
                              </td>
                              <td className="p-3 text-on-surface font-medium">
                                {salon.licenseExpiryDate ? new Date(salon.licenseExpiryDate).toLocaleDateString("tr-TR") : "Belirtilmemiş"}
                              </td>
                              <td className="p-3">
                                <select
                                  value={adminPackages.find(p => p.name === salon.packageName)?.id || ""}
                                  onChange={(e) => handleUpgradePackage(salon.id, e.target.value)}
                                  className="bg-transparent border border-outline-variant/60 rounded px-1.5 py-0.5 text-[11px] font-semibold text-on-surface outline-none focus:border-primary"
                                >
                                  {adminPackages.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => handleRenewLicense(salon.id, 1)}
                                    className="bg-primary/5 border border-primary/10 hover:border-primary/30 text-primary font-bold px-2 py-1 rounded text-[10px] hover:bg-primary/10 cursor-pointer"
                                  >
                                    +1 Ay
                                  </button>
                                  <button
                                    onClick={() => handleRenewLicense(salon.id, 3)}
                                    className="bg-primary/5 border border-primary/10 hover:border-primary/30 text-primary font-bold px-2 py-1 rounded text-[10px] hover:bg-primary/10 cursor-pointer"
                                  >
                                    +3 Ay
                                  </button>
                                  <button
                                    onClick={() => handleRenewLicense(salon.id, 12)}
                                    className="bg-primary/5 border border-primary/10 hover:border-primary/30 text-primary font-bold px-2 py-1 rounded text-[10px] hover:bg-primary/10 cursor-pointer"
                                  >
                                    +12 Ay
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Activity Sidebar Column (1/4 width - ince uzun en sağda) */}
        <div className="lg:col-span-1 h-full">
          {user?.role === "ADMIN" && (
            <div className="bg-white rounded-3xl border border-outline-variant p-6 shadow-sm space-y-4 h-full min-h-[500px] flex flex-col">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-success/10 text-success flex items-center justify-center">
                  <Icon name="sensors" className="!text-[18px]" />
                </div>
                <h3 className="font-bold text-base text-on-surface">Aktiflik (Online)</h3>
              </div>
              <p className="text-xs text-on-surface-variant">Şu an sistemde işlem yapan aktif salonlar.</p>

              <div className="divide-y divide-outline-variant overflow-y-auto pr-1 flex-1 max-h-[550px]">
                {onlineSalons.length === 0 ? (
                  <div className="py-8 text-center text-xs text-on-surface-variant font-medium">
                    Çevrimiçi salon bulunmuyor.
                  </div>
                ) : (
                  onlineSalons.map((s) => (
                    <div key={s.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
                        </span>
                        <span className="text-xs font-semibold text-on-surface truncate">{s.name}</span>
                      </div>
                      <span className="text-[10px] text-on-surface-variant font-medium flex-shrink-0">
                        {new Date(s.lastActivity).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {user?.role === "MANAGER" && (
            <div className="bg-white rounded-3xl border border-outline-variant p-6 shadow-sm space-y-4 h-full min-h-[500px] flex flex-col">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="groups" className="!text-[18px]" />
                </div>
                <h3 className="font-bold text-base text-on-surface">Personel Aktifliği</h3>
              </div>
              <p className="text-xs text-on-surface-variant">Personellerin çevrimiçi durumu ve bugünkü işlem sayıları.</p>

              <div className="divide-y divide-outline-variant overflow-y-auto pr-1 flex-1 max-h-[550px]">
                {staffActivities.length === 0 ? (
                  <div className="py-8 text-center text-xs text-on-surface-variant font-medium">
                    Kayıtlı personel bulunmuyor.
                  </div>
                ) : (
                  staffActivities.map((s) => (
                    <div key={s.staffId} className="py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {s.online ? (
                          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
                          </span>
                        ) : (
                          <span className="h-2.5 w-2.5 rounded-full bg-outline-variant flex-shrink-0"></span>
                        )}
                        <div className="truncate leading-tight flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-semibold text-on-surface truncate">{s.fullName}</span>
                          <span className="text-[9px] bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.2 rounded-full font-bold flex-shrink-0">
                            {s.requestCount}
                          </span>
                        </div>
                      </div>
                      {s.lastActivity ? (
                        <span className="text-[9px] text-on-surface-variant font-medium flex-shrink-0">
                          {new Date(s.lastActivity).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      ) : (
                        <span className="text-[9px] text-on-surface-variant font-medium flex-shrink-0">
                          Pasif
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
